import React, { useMemo, useState } from "react";
import { motion } from "framer-motion";

/** Example catalogue — Stripe checkout coming soon (no live charges yet). */
const PRINTS = [
  {
    id: "dragon-bust",
    sku: "Y666-PRT-001",
    name: "NFG Dragon Bust",
    blurb: "Desktop-size dragon bust. Matte black or gold filament.",
    price: 28,
    badge: "Featured",
    category: "Figures",
    accent: "#B56CFF",
    options: ["Matte Black", "Gold Trim"],
    stock: "Coming soon",
  },
  {
    id: "crash-rocket",
    sku: "Y666-PRT-002",
    name: "Crash Rocket Desk Toy",
    blurb: "Print-in-place rocket. Matches the NFG Crash chart vibe.",
    price: 18,
    badge: "Popular",
    category: "Desk toys",
    accent: "#E8C547",
    options: ["Black / Gold", "Purple / Black"],
    stock: "Coming soon",
  },
  {
    id: "signal-keychain",
    sku: "Y666-PRT-003",
    name: "Y666 Signal Keychain",
    blurb: "Compact brand tag in durable PETG. Gift-ready.",
    price: 9,
    badge: "Starter",
    category: "Accessories",
    accent: "#A855F7",
    options: ["Black", "Gold"],
    stock: "Coming soon",
  },
  {
    id: "custom-commission",
    sku: "Y666-PRT-CUS",
    name: "Custom Commission",
    blurb: "Your design or remix. Quote after brief.",
    price: 35,
    pricePrefix: "From ",
    badge: "Custom",
    category: "Custom",
    accent: "#F0C75E",
    options: ["1-off", "Small run"],
    stock: "Coming soon",
  },
  {
    id: "fuse-plaque",
    sku: "Y666-PRT-004",
    name: "Fuse Wall Plaque",
    blurb: "Layered NFG Fuse plaque for desk or wall mount.",
    price: 22,
    badge: "New",
    category: "Figures",
    accent: "#E8C547",
    options: ["Black", "White"],
    stock: "Coming soon",
  },
  {
    id: "streamer-stand",
    sku: "Y666-PRT-005",
    name: "Phone Stream Stand",
    blurb: "Angle-adjust desk stand for live streams.",
    price: 16,
    badge: "Utility",
    category: "Desk toys",
    accent: "#FFFFFF",
    options: ["Compact", "Tall"],
    stock: "Coming soon",
  },
];

const CATEGORIES = ["All", "Figures", "Desk toys", "Accessories", "Custom"];

function formatPrice(item) {
  return `${item.pricePrefix || ""}£${item.price}`;
}

function ProductPhoto({ item, large = false }) {
  return (
    <div
      className={`relative overflow-hidden border border-white/10 bg-[#0a0a12] ${
        large ? "aspect-[5/4] md:aspect-[4/3]" : "aspect-square"
      }`}
      style={{
        backgroundImage: `
          radial-gradient(ellipse at 50% 38%, ${item.accent}28, transparent 58%),
          linear-gradient(165deg, #10101a 0%, #07070c 55%, #050508 100%)
        `,
      }}
    >
      {/* Fake product silhouette so the frame reads as a photo slot */}
      <div className="absolute inset-0 flex items-center justify-center p-6 md:p-8">
        <div
          className={`relative ${large ? "h-[58%] w-[46%]" : "h-[62%] w-[52%]"} max-w-[220px]`}
          style={{
            background: `linear-gradient(160deg, ${item.accent}33, rgba(255,255,255,0.04) 45%, transparent)`,
            clipPath: "polygon(18% 8%, 82% 8%, 94% 28%, 88% 92%, 12% 92%, 6% 28%)",
            boxShadow: `0 18px 40px ${item.accent}22, inset 0 0 30px rgba(0,0,0,0.35)`,
            border: `1px solid ${item.accent}44`,
          }}
          aria-hidden
        />
      </div>

      {/* Soft floor shadow */}
      <div
        className="pointer-events-none absolute bottom-[14%] left-1/2 h-3 w-[42%] -translate-x-1/2 rounded-[100%] bg-black/50 blur-md"
        aria-hidden
      />

      {/* Coming soon stamp over the image */}
      <div className="absolute inset-0 flex items-center justify-center bg-black/35 backdrop-blur-[1px]">
        <div
          className={`border border-white/30 bg-black/70 px-3 py-2 text-center shadow-lg shadow-black/50 ${
            large ? "rotate-[-8deg]" : "rotate-[-10deg]"
          }`}
        >
          <p className="font-mono text-[0.55rem] uppercase tracking-[0.35em] text-zinc-400">
            Product image
          </p>
          <p
            className={`font-display font-black uppercase tracking-[0.18em] text-white ${
              large ? "text-xl md:text-2xl" : "text-sm md:text-base"
            }`}
          >
            Coming soon
          </p>
        </div>
      </div>

      <span
        className="absolute top-3 left-3 z-[1] font-mono text-[0.55rem] uppercase tracking-[0.24em] px-2 py-1 border bg-black/65"
        style={{ borderColor: `${item.accent}66`, color: item.accent }}
      >
        {item.badge}
      </span>
    </div>
  );
}

export default function PrintsShop() {
  const [category, setCategory] = useState("All");
  const [selectedId, setSelectedId] = useState(PRINTS[0].id);
  const [optionIdx, setOptionIdx] = useState(0);
  const [qty, setQty] = useState(1);

  const filtered = useMemo(
    () => (category === "All" ? PRINTS : PRINTS.filter((p) => p.category === category)),
    [category]
  );

  const selected = useMemo(
    () => filtered.find((p) => p.id === selectedId) || filtered[0] || PRINTS[0],
    [filtered, selectedId]
  );

  const selectProduct = (id) => {
    setSelectedId(id);
    setOptionIdx(0);
    setQty(1);
  };

  return (
    <section
      id="prints"
      data-testid="prints-section"
      className="relative py-10 md:py-14 overflow-hidden"
    >
      <div
        className="pointer-events-none absolute -top-24 right-0 h-72 w-72 rounded-full opacity-40"
        style={{ background: "radial-gradient(closest-side, rgba(168,85,247,0.28), transparent)" }}
        aria-hidden
      />
      <div
        className="pointer-events-none absolute -bottom-28 left-0 h-80 w-80 rounded-full opacity-35"
        style={{ background: "radial-gradient(closest-side, rgba(181,108,255,0.2), transparent)" }}
        aria-hidden
      />

      <div className="relative mx-auto max-w-7xl px-6 md:px-10">
        {/* Store header */}
        <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6 mb-8 md:mb-10">
          <div className="min-w-0">
            <span className="label-tag text-purple-300">// MERCH · 3D PRINTS</span>
            <h2 className="font-display font-black uppercase text-4xl md:text-6xl tracking-tighter mt-3">
              PRINT <span className="neon-text-cyan">STORE</span>
            </h2>
            <p className="mt-4 text-zinc-400 max-w-xl">
              Store layout preview — product photos arrive when prints go live. Browse the
              catalogue, pick options, and see how Stripe checkout will sit.
            </p>
          </div>
          <div className="shrink-0 flex flex-col items-start lg:items-end gap-2">
            <span
              data-testid="prints-coming-soon-badge"
              className="inline-flex items-center gap-2 border border-amber-400/50 bg-amber-500/10 px-4 py-2 font-mono text-[0.65rem] uppercase tracking-[0.28em] text-amber-200"
            >
              <span className="h-1.5 w-1.5 rounded-full bg-amber-400 animate-pulse" />
              Store opening soon
            </span>
            <span className="font-mono text-[0.6rem] uppercase tracking-[0.22em] text-zinc-500">
              Stripe · UK shipping · {PRINTS.length} SKUs
            </span>
          </div>
        </div>

        {/* Store chrome: categories + fake search / cart */}
        <div className="mb-6 md:mb-8 flex flex-col gap-3 border border-white/10 bg-black/50 p-3 md:p-4">
          <div className="flex flex-col md:flex-row md:items-center gap-3">
            <div className="flex flex-wrap gap-2 flex-1 min-w-0">
              {CATEGORIES.map((cat) => {
                const active = category === cat;
                return (
                  <button
                    key={cat}
                    type="button"
                    onClick={() => setCategory(cat)}
                    className={`font-mono text-[0.6rem] uppercase tracking-[0.22em] px-3 py-2 border transition-colors ${
                      active
                        ? "border-purple-400/60 bg-purple-400/10 text-purple-200"
                        : "border-white/10 text-zinc-500 hover:text-zinc-300 hover:border-white/20"
                    }`}
                  >
                    {cat}
                  </button>
                );
              })}
            </div>
            <div className="flex items-center gap-2 shrink-0">
              <div className="flex-1 md:w-48 border border-white/10 bg-black/40 px-3 py-2 font-mono text-[0.65rem] uppercase tracking-[0.18em] text-zinc-600">
                Search prints…
              </div>
              <div className="border border-white/10 bg-black/40 px-3 py-2 font-mono text-[0.6rem] uppercase tracking-[0.2em] text-zinc-500">
                Bag · 0
              </div>
            </div>
          </div>
          <div className="flex items-center justify-between gap-3 font-mono text-[0.58rem] uppercase tracking-[0.2em] text-zinc-600 border-t border-white/5 pt-3">
            <span>
              Showing {filtered.length} / {PRINTS.length} products
            </span>
            <span>Sort · Featured</span>
          </div>
        </div>

        {/* Main store layout: gallery + PDP + cart strip */}
        <div className="grid grid-cols-1 xl:grid-cols-12 gap-6 md:gap-7">
          {/* Product detail panel */}
          <motion.div
            key={selected.id}
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.35 }}
            className="xl:col-span-7 border border-white/10 bg-black/55 overflow-hidden"
            data-testid={`print-detail-${selected.id}`}
          >
            <ProductPhoto item={selected} large />

            <div className="p-5 md:p-7">
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div className="min-w-0">
                  <p className="font-mono text-[0.58rem] uppercase tracking-[0.28em] text-zinc-500">
                    {selected.sku} · {selected.category}
                  </p>
                  <h3 className="mt-2 font-display font-black uppercase text-2xl md:text-3xl tracking-tight text-white">
                    {selected.name}
                  </h3>
                </div>
                <div className="text-right shrink-0">
                  <p className="font-mono text-2xl text-purple-300">{formatPrice(selected)}</p>
                  <p className="mt-1 font-mono text-[0.55rem] uppercase tracking-[0.2em] text-zinc-500">
                    {selected.stock}
                  </p>
                </div>
              </div>

              <p className="mt-4 text-sm text-zinc-400 leading-relaxed max-w-2xl">{selected.blurb}</p>

              <div className="mt-6">
                <p className="font-mono text-[0.58rem] uppercase tracking-[0.24em] text-zinc-500 mb-2">
                  Finish
                </p>
                <div className="flex flex-wrap gap-2">
                  {selected.options.map((opt, i) => (
                    <button
                      key={opt}
                      type="button"
                      onClick={() => setOptionIdx(i)}
                      className={`px-3 py-2 border font-mono text-[0.6rem] uppercase tracking-[0.18em] transition-colors ${
                        optionIdx === i
                          ? "border-purple-400/50 text-purple-200 bg-purple-400/10"
                          : "border-white/10 text-zinc-500 hover:border-white/25"
                      }`}
                    >
                      {opt}
                    </button>
                  ))}
                </div>
              </div>

              <div className="mt-6 flex flex-col sm:flex-row sm:items-center gap-3">
                <div className="inline-flex items-center border border-white/10 bg-black/40">
                  <button
                    type="button"
                    className="px-3 py-2.5 text-zinc-400 hover:text-white"
                    onClick={() => setQty((q) => Math.max(1, q - 1))}
                    aria-label="Decrease quantity"
                  >
                    −
                  </button>
                  <span className="min-w-[2.5rem] text-center font-mono text-sm text-white">{qty}</span>
                  <button
                    type="button"
                    className="px-3 py-2.5 text-zinc-400 hover:text-white"
                    onClick={() => setQty((q) => Math.min(9, q + 1))}
                    aria-label="Increase quantity"
                  >
                    +
                  </button>
                </div>

                <button
                  type="button"
                  disabled
                  data-testid={`print-buy-${selected.id}`}
                  className="flex-1 border border-white/15 bg-white/5 px-4 py-3 font-mono text-[0.65rem] uppercase tracking-[0.24em] text-zinc-500 cursor-not-allowed"
                  title="Stripe checkout coming soon"
                >
                  Add to bag · Coming soon
                </button>
                <button
                  type="button"
                  disabled
                  className="border border-purple-400/30 bg-purple-400/5 px-4 py-3 font-mono text-[0.65rem] uppercase tracking-[0.24em] text-purple-500/70 cursor-not-allowed"
                >
                  Buy now
                </button>
              </div>
            </div>
          </motion.div>

          {/* Catalogue grid + bag summary */}
          <div className="xl:col-span-5 flex flex-col gap-5">
            <div className="grid grid-cols-2 gap-3 sm:gap-4">
              {filtered.map((item, i) => {
                const active = item.id === selected.id;
                return (
                  <motion.button
                    key={item.id}
                    type="button"
                    initial={{ opacity: 0, y: 16 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, amount: 0.15 }}
                    transition={{ duration: 0.4, delay: i * 0.04 }}
                    onClick={() => selectProduct(item.id)}
                    data-testid={`print-card-${item.id}`}
                    className={`group text-left border overflow-hidden bg-black/55 transition-colors ${
                      active
                        ? "border-purple-400/50 ring-1 ring-purple-400/30"
                        : "border-white/10 hover:border-white/25"
                    }`}
                  >
                    <ProductPhoto item={item} />
                    <div className="p-3 md:p-4">
                      <div className="flex items-start justify-between gap-2">
                        <h4 className="font-display font-bold uppercase text-sm tracking-tight text-white leading-snug line-clamp-2">
                          {item.name}
                        </h4>
                        <span className="shrink-0 font-mono text-xs text-purple-300">
                          {formatPrice(item)}
                        </span>
                      </div>
                      <p className="mt-2 font-mono text-[0.55rem] uppercase tracking-[0.2em] text-zinc-600">
                        {item.category}
                      </p>
                    </div>
                  </motion.button>
                );
              })}
            </div>

            {/* Checkout / bag preview */}
            <div
              data-testid="prints-stripe-note"
              className="border border-purple-400/25 bg-gradient-to-br from-purple-500/5 via-black/50 to-amber-500/5 p-5 md:p-6"
            >
              <div className="flex items-center justify-between gap-3 mb-4">
                <p className="font-display font-bold uppercase text-sm tracking-wide text-white">
                  Your bag
                </p>
                <span className="font-mono text-[0.55rem] uppercase tracking-[0.22em] text-zinc-500">
                  Preview only
                </span>
              </div>

              <div className="flex gap-3 border border-white/10 bg-black/40 p-3 mb-4 opacity-80">
                <div className="h-16 w-16 shrink-0 border border-white/10 bg-[#0a0a12] relative overflow-hidden">
                  <div className="absolute inset-0 flex items-center justify-center">
                    <span className="font-mono text-[0.45rem] uppercase tracking-[0.15em] text-zinc-500 text-center px-1">
                      Coming soon
                    </span>
                  </div>
                </div>
                <div className="min-w-0 flex-1">
                  <p className="font-display font-bold uppercase text-xs text-white truncate">
                    {selected.name}
                  </p>
                  <p className="mt-1 font-mono text-[0.55rem] uppercase tracking-[0.18em] text-zinc-500">
                    {selected.options[optionIdx]} · Qty {qty}
                  </p>
                  <p className="mt-2 font-mono text-sm text-purple-300">
                    £{(selected.price * qty).toFixed(0)}
                  </p>
                </div>
              </div>

              <div className="space-y-2 font-mono text-[0.65rem] uppercase tracking-[0.16em] text-zinc-500 mb-5">
                <div className="flex justify-between">
                  <span>Subtotal</span>
                  <span>£{(selected.price * qty).toFixed(0)}</span>
                </div>
                <div className="flex justify-between">
                  <span>Shipping</span>
                  <span>Calc at checkout</span>
                </div>
                <div className="flex justify-between border-t border-white/10 pt-2 text-zinc-300">
                  <span>Total</span>
                  <span>£{(selected.price * qty).toFixed(0)}</span>
                </div>
              </div>

              <button
                type="button"
                disabled
                className="w-full border border-white/15 bg-white/5 px-4 py-3 font-mono text-[0.65rem] uppercase tracking-[0.24em] text-zinc-500 cursor-not-allowed"
              >
                Checkout with Stripe · Coming soon
              </button>
              <p className="mt-3 text-xs text-zinc-500 leading-relaxed">
                Cards, Apple Pay, and Google Pay will run through Stripe when the store opens.
                Nothing is chargeable yet.
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
